/** @app — application layer; owned per-fork (LMS / OTT). See PLATFORM_FILES.md. */
import type { LearningCourseDetail } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';

interface RatingHistogramProps {
  rating?: number;
  ratingCount?: number;
  /** 5-tuple [1★ count, 2★, 3★, 4★, 5★] — schema enforces 5-tuple. */
  distribution: NonNullable<LearningCourseDetail['reviewSummary']>['distribution'];
}

export function RatingHistogram({ rating, ratingCount, distribution }: RatingHistogramProps) {
  const total = distribution.reduce((s, n) => s + n, 0);
  // Render high-to-low (5 stars first) since users scan that way.
  const rows = [5, 4, 3, 2, 1].map((stars) => {
    const count = distribution[stars - 1] ?? 0;
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return { stars, count, pct };
  });

  return (
    <Card asChild>
      <div className="cd-rating-block">
        <div className="cd-rating-summary">
          <div className="cd-rating-big">{rating?.toFixed(1) ?? '—'}</div>
          <div className="cd-rating-stars-big" aria-hidden>
            {'★'.repeat(Math.round(rating ?? 0))}
            {'☆'.repeat(5 - Math.round(rating ?? 0))}
          </div>
          {ratingCount !== undefined && (
            <div className="cd-rating-count">
              {ratingCount.toLocaleString('tr-TR')} değerlendirme
            </div>
          )}
        </div>
        <div className="cd-rating-bars">
          {rows.map((r) => (
            <div key={r.stars} className="cd-rating-bar-row">
              <span>{r.stars}★</span>
              <div className="cd-rating-bar-track">
                <div className="cd-rating-bar-fill" style={{ width: `${r.pct}%` }} />
              </div>
              <span>{r.pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
